// Two-step login screens (admin PR 2): the code step of an admin login, the first
// setup (QR code → first code → backup codes), and the pieces Settings reuses.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, twoStepLogin, twoStepSetupFinish, twoStepSetupStart, type TwoStepSecret, type User } from '../api';
import { backupCodesText, backupFileName, cleanAppCode, cleanBackupCode, qrCode } from '../two-step';

const errText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);
/** The server ended the half-finished login (too slow, too many tries, paused): back to the password. */
const loginIsOver = (err: unknown) => err instanceof ApiError && (err.code === 'ticket_expired' || err.code === 'two_step_paused');

/** The 6-digit box (or, with `backup`, the box for a backup code like k7qm-x2pd). */
export function CodeBox({
  value,
  onChange,
  backup = false,
  label,
  autoFocus = true,
  name = 'code',
  onLetters,
}: {
  value: string;
  onChange: (v: string) => void;
  backup?: boolean;
  label: string;
  autoFocus?: boolean;
  name?: string;
  /** letters typed / pasted into the 6-digit box: it's a backup code (switch boxes, keep what was typed) */
  onLetters?: (typed: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {backup ? (
        <input
          className="code-input code-input-backup"
          name={`${name}-backup`}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={11}
          placeholder="xxxx-xxxx"
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className="code-input"
          name={name}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={onLetters ? 11 : 6}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (onLetters && /[a-z]/i.test(raw)) onLetters(raw);
            else onChange(cleanAppCode(raw));
          }}
        />
      )}
    </label>
  );
}

/** The code typed is complete enough to send. */
export const codeReady = (value: string, backup: boolean) => (backup ? cleanBackupCode(value) !== null : value.length === 6);

/** QR code + the key as text (for typing into the app by hand). */
export function SecretView({ data }: { data: TwoStepSecret }) {
  const [qr, setQr] = useState<{ path: string; viewBox: number } | null>(null);
  const [qrFailed, setQrFailed] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let live = true;
    qrCode(data.uri).then(
      (q) => live && setQr(q),
      () => live && (setQrFailed(true), setShowKey(true)),
    );
    return () => {
      live = false;
    };
  }, [data.uri]);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(data.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setShowKey(true);
    }
  };

  return (
    <div className="ts-secret">
      <div className="ts-qr" aria-hidden={!qr}>
        {qr ? (
          <svg viewBox={`0 0 ${qr.viewBox} ${qr.viewBox}`} role="img" aria-label="QR code for your authenticator app" shapeRendering="crispEdges">
            {/* always dark on white: phone cameras need it, also in dark mode */}
            <rect width={qr.viewBox} height={qr.viewBox} fill="#ffffff" />
            <path d={qr.path} fill="#000000" />
          </svg>
        ) : (
          !qrFailed && <span className="ts-qr-wait">Making the QR code…</span>
        )}
      </div>
      {showKey ? (
        <div className="ts-key">
          <span className="ts-key-label">
            {qrFailed ? 'Enter this key in your authenticator app' : 'Or enter this key in the app'} ({data.issuer} · {data.account}, time-based):
          </span>
          <code className="ts-key-text">{data.secretGrouped}</code>
          <button className="btn btn-small" type="button" onClick={() => void copyKey()}>
            {copied ? 'Copied' : 'Copy key'}
          </button>
        </div>
      ) : (
        <button className="link-btn ts-cant-scan" type="button" onClick={() => setShowKey(true)}>
          Can’t scan it? Enter a key instead
        </button>
      )}
    </div>
  );
}

/** The 10 backup codes, shown once, with Copy and Download. */
export function BackupCodesView({ codes, account }: { codes: string[]; account: string }) {
  const [copied, setCopied] = useState(false);
  const text = backupCodesText(codes, account);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the list stays on screen to write down */
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = backupFileName();
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="ts-backup">
      <ol className="ts-codes" aria-label="Backup codes">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <div className="ts-backup-actions">
        <button className="btn btn-small" type="button" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy all'}
        </button>
        <button className="btn btn-small" type="button" onClick={download}>
          Download (.txt)
        </button>
      </div>
    </div>
  );
}

/** Admin login, step 2: the code from the authenticator app (or a backup code). */
export function TwoStepCode({
  ticket,
  email,
  onDone,
  onOver,
}: {
  ticket: string;
  email: string;
  onDone: (u: User, note?: string) => void;
  /** back to the password step (with a message) */
  onOver: (message?: string) => void;
}) {
  const [backup, setBackup] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sent = useRef('');

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (busy || !codeReady(code, backup)) return;
    const value = backup ? cleanBackupCode(code)! : code;
    sent.current = value;
    setBusy(true);
    setError(null);
    try {
      const r = await twoStepLogin(ticket, value);
      const left = r.backupCodesLeft;
      onDone(
        r.user,
        left === undefined
          ? undefined
          : left === 0
            ? 'You used your last backup code. Make new ones now in Settings → Account → Two-step login.'
            : left <= 2
              ? `Only ${left} backup code${left === 1 ? '' : 's'} left. Make new ones in Settings → Account → Two-step login.`
              : undefined,
      );
    } catch (err) {
      if (loginIsOver(err)) {
        onOver((err as ApiError).message);
        return;
      }
      setError(errText(err, 'Couldn’t check the code. Try again.'));
      setCode('');
      setBusy(false);
    }
  }

  // a full 6-digit code is sent at once (no extra press; the box clears on a wrong one)
  useEffect(() => {
    if (!backup && code.length === 6 && code !== sent.current) void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, backup]);

  return (
    <form className="login-card-body" onSubmit={submit} noValidate>
      <h1>Two-step login</h1>
      <p className="login-notice">
        {backup ? (
          <>Enter one of your backup codes for <span className="login-email">{email}</span>. Each code works once.</>
        ) : (
          <>Enter the 6-digit code from your authenticator app for <span className="login-email">{email}</span>.</>
        )}
      </p>
      <CodeBox
        key={backup ? 'b' : 'a'}
        value={code}
        onChange={setCode}
        backup={backup}
        label={backup ? 'Backup code' : 'Code'}
        onLetters={(typed) => {
          setBackup(true);
          setCode(typed);
          setError(null);
          sent.current = '';
        }}
      />
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}
      <button className="btn btn-primary btn-block" type="submit" disabled={busy || !codeReady(code, backup)}>
        {busy ? 'Checking…' : 'Log in'}
      </button>
      <button
        className="link-btn login-back"
        type="button"
        onClick={() => {
          setBackup(!backup);
          setCode('');
          setError(null);
          sent.current = '';
        }}
      >
        {backup ? 'Use the code from my app' : 'Use a backup code'}
      </button>
      <p className="login-help">
        Lost your phone and your backup codes? Someone with access to the server can reset your two-step login.{' '}
        <button className="link-btn" type="button" onClick={() => onOver()}>
          Back to log in
        </button>
      </p>
    </form>
  );
}

/** Admin login, first time: set up the authenticator app, then save the backup codes. */
export function TwoStepSetup({
  ticket,
  email,
  onDone,
  onOver,
}: {
  ticket: string;
  email: string;
  onDone: (u: User) => void;
  onOver: (message?: string) => void;
}) {
  const [data, setData] = useState<TwoStepSecret | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ user: User; codes: string[] } | null>(null);
  const [saved, setSaved] = useState(false);
  // the setup couldn't start (off the office network / VPN, …): no steps, no code box, just why
  const [startError, setStartError] = useState<string | null>(null);

  useEffect(() => {
    twoStepSetupStart(ticket).then(setData, (err) => {
      if (loginIsOver(err) || (err instanceof ApiError && err.code === 'already_set_up')) onOver((err as ApiError).message);
      else setStartError(errText(err, 'Couldn’t start the setup. Go back and log in again.'));
    });
  }, [ticket, onOver]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const r = await twoStepSetupFinish(ticket, code);
      setDone({ user: r.user, codes: r.backupCodes });
    } catch (err) {
      if (loginIsOver(err) || (err instanceof ApiError && err.code === 'already_set_up')) {
        onOver((err as ApiError).message);
        return;
      }
      setError(errText(err, 'Couldn’t check the code. Try again.'));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="login-card-body">
        <h1>Save your backup codes</h1>
        <p className="login-notice">
          Two-step login is on. If you ever can’t use your phone, each of these codes logs you in once. They are shown only now.
        </p>
        <BackupCodesView codes={done.codes} account={email} />
        <p className="setting-note">Keep them somewhere safe, like a password manager — not on the same phone as the app.</p>
        <label className="ts-check">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
          <span>I’ve saved my backup codes</span>
        </label>
        <button className="btn btn-primary btn-block" type="button" disabled={!saved} onClick={() => onDone(done.user)}>
          Continue
        </button>
      </div>
    );
  }

  if (startError) {
    return (
      <div className="login-card-body">
        <h1>Set up two-step login</h1>
        <p className="login-error" role="alert">
          {startError}
        </p>
        <button className="btn btn-primary btn-block" type="button" onClick={() => onOver()}>
          Back to log in
        </button>
      </div>
    );
  }

  // until the server answers, nothing to scan or type: off the VPN the answer is a refusal,
  // and the steps flashing first looked wrong (owner's video, session 22)
  if (!data) {
    return (
      <div className="login-card-body" aria-busy="true">
        <h1>Set up two-step login</h1>
        <p className="login-notice">Preparing…</p>
      </div>
    );
  }

  return (
    <form className="login-card-body" onSubmit={submit} noValidate>
      <h1>Set up two-step login</h1>
      <p className="login-notice">
        Admin accounts need a second step: a code from an authenticator app on your phone (Google or Microsoft Authenticator, 1Password, …).
      </p>
      <ol className="ts-steps">
        <li>Open the app and add an account (“+” or “Scan a QR code”).</li>
        <li>Scan this code. Tip: add it on a second device too, in case you lose this phone.</li>
        <li>Type the 6-digit code the app shows for CloudPhone.</li>
      </ol>
      <SecretView data={data} />
      <CodeBox value={code} onChange={setCode} label="Code from the app" autoFocus={false} />
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}
      <button className="btn btn-primary btn-block" type="submit" disabled={busy || code.length !== 6}>
        {busy ? 'Checking…' : 'Turn on two-step login'}
      </button>
      <button className="link-btn login-back" type="button" onClick={() => onOver()}>
        Back to log in
      </button>
    </form>
  );
}
